import React from 'react'
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  ReferenceInput,
  ArrayInput,
  SimpleFormIterator,
  BooleanInput,
  useRecordContext,
  Labeled,
  TextField,
  DateField,
  TopToolbar,
  Toolbar,
  SaveButton,
  DeleteButton,
  ListButton,
  FormDataConsumer,
  useDataProvider,
  useNotify,
  useRefresh,
  useRedirect,
} from 'react-admin'
import {
  Grid,
  Box,
  Typography,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Chip,
  Button,
  TextField as MuiTextField,
  MenuItem,
  Stack,
  Autocomplete,
} from '@mui/material'
import {
  InformationCircleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

const UNIVERSAL_FIELDS = [
  'Supplier Name',
  'Signatory Details',
  'Contract Type',
  'Categorisation',
  'Linked Contracts',
  'Parent Agreement',
  'Referenced Schedules',
  'Effective Date',
  'Expiration Date',
  'Auto-Renewal Clause',
  'Notice Period',
  'Payment Terms',
  'Contract Value',
  'Liability Cap',
  'Indemnification',
  'Governing Law',
  'Dispute Resolution',
  'Termination Rights',
  'Change of Control',
  'Data Privacy',
  'Confidentiality',
  'Force Majeure',
  'Insurance',
  'Assignment Rights',
]

const getConfidenceColor = (confidence) => {
  if (confidence >= 80) return '#16a34a'
  if (confidence >= 50) return '#d97706'
  return '#dc2626'
}

const isFieldMandatory = (fieldName) => {
  if (!fieldName) return false
  const normalizedFieldName = fieldName.toLowerCase()
  return UNIVERSAL_FIELDS.some(
    (uf) =>
      normalizedFieldName.includes(uf.toLowerCase()) ||
      uf.toLowerCase().includes(normalizedFieldName)
  )
}

const hasUsableFieldValue = (value) => {
  if (value === null || value === undefined) return false
  const normalized = String(value).trim()
  return normalized !== '' && normalized !== 'None' && normalized !== 'null'
}

const getMetadataSortRank = (field) => {
  const isMandatory = isFieldMandatory(field?.field_name)
  const isFilled = hasUsableFieldValue(field?.field_value)
  if (isMandatory && isFilled) return 0
  if (isMandatory && !isFilled) return 1
  if (!isMandatory && isFilled) return 2
  return 3
}

const MetadataFieldLabel = ({ field }) => {
  const isMandatory = isFieldMandatory(field.field_name)

  return (
    <Box>
      <Box display="flex" alignItems="flex-start" gap={0.75} flexWrap="wrap">
        <Typography
          variant="subtitle2"
          component="span"
          sx={{
            fontWeight: 600,
            color: '#1f2937',
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'unset',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            lineHeight: 1.3,
            maxWidth: '100%',
          }}
        >
          {field.field_name}
        </Typography>
        {field.evidence_quote && (
          <Tooltip title={field.evidence_quote} arrow placement="top">
            <IconButton
              size="small"
              sx={{
                color: '#6b7280',
                p: 0.25,
                '&:hover': { backgroundColor: '#f3f4f6' },
              }}
            >
              <InformationCircleIcon className="h-4 w-4 text-stone-400" />
            </IconButton>
          </Tooltip>
        )}
        {field.is_verified && (
          <CheckCircleIcon className="h-4 w-4 text-green-500" />
        )}
      </Box>
      {isMandatory && (
        <Chip
          label="CORE REVIEW"
          size="small"
          sx={{
            mt: 0.5,
            height: 20,
            fontSize: '0.65rem',
            backgroundColor: '#7c2d12',
            color: '#fff',
            fontWeight: 'bold',
            letterSpacing: '0.04em',
          }}
        />
      )}
    </Box>
  )
}

const ContractSummaryHeader = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [loading, setLoading] = React.useState(false)
  const [previewLoading, setPreviewLoading] = React.useState(false)

  if (!record) return null

  const handleRetry = () => {
    setLoading(true)
    dataProvider
      .retryExtraction(record.id)
      .then(() => {
        notify('Extraction retried successfully', { type: 'info' })
        refresh()
      })
      .catch((error) => {
        notify(`Error retrying extraction: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handlePreview = () => {
    if (!record?.id || previewLoading) return
    setPreviewLoading(true)
    dataProvider
      .getContractPreviewBlob(record.id)
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob)
        window.open(blobUrl, '_blank', 'noopener,noreferrer')
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
      })
      .catch((error) => {
        notify(`Error opening preview: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setPreviewLoading(false)
      })
  }

  const statusLabelMap = {
    uploaded: 'UPLOADED',
    processing: 'PROCESSING',
    review_required: 'REVIEW REQUIRED',
    completed: 'VERIFIED',
    failed: 'FAILED',
    extraction_failed: 'EXTRACTION FAILED',
    missing_source_file: 'MISSING SOURCE FILE',
  }

  const stats = {
    total: record.metadata_fields?.length || 0,
    verified: record.metadata_fields?.filter((f) => f.is_verified).length || 0,
    confidence:
      typeof record.ai_confidence === 'number'
        ? Math.round(record.ai_confidence * 100)
        : 0,
  }
  const terminalNoDataFailure =
    typeof record.extraction_error_message === 'string' &&
    (record.extraction_error_message.includes(
      'No usable metadata values extracted from document'
    ) ||
      record.extraction_error_message.includes(
        'Unsupported PDF portfolio format'
      ))
  const canRetry =
    record.status === 'uploaded' ||
    record.status === 'extraction_failed' ||
    (record.status === 'failed' && !terminalNoDataFailure) ||
    (record.status === 'review_required' && stats.total === 0)

  const confidenceColor = getConfidenceColor(stats.confidence)

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 3,
        borderColor: '#e5e7eb',
        borderRadius: 2,
        background:
          'linear-gradient(135deg, rgba(249,250,251,1) 0%, rgba(255,255,255,1) 100%)',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="overline" color="textSecondary">
              Contract Name
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
              {record.filename}
            </Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="overline" color="textSecondary">
              Avg Confidence
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: confidenceColor,
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {stats.confidence}%
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="overline" color="textSecondary">
              Verification
            </Typography>
            <Typography variant="h6">
              {stats.verified} / {stats.total}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography variant="overline" color="textSecondary">
                  Current Status
                </Typography>
                <Box mt={0.5}>
                  <Chip
                    label={statusLabelMap[record.status] || 'UNKNOWN'}
                    color={
                      record.status === 'failed' ||
                      record.status === 'extraction_failed'
                        ? 'error'
                        : record.status === 'review_required'
                        ? 'warning'
                        : record.status === 'completed'
                        ? 'success'
                        : 'default'
                    }
                    variant="filled"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                {(record.status === 'uploaded' ||
                  record.status === 'processing') &&
                  (record.extraction_stage_label ||
                    record.extraction_status_message) && (
                    <Box mt={1}>
                      {record.extraction_stage_label && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          {record.extraction_stage_label}
                          {record.extraction_stage_index &&
                          record.extraction_stage_total
                            ? ` (${record.extraction_stage_index}/${record.extraction_stage_total})`
                            : ''}
                        </Typography>
                      )}
                      {record.extraction_status_message && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          {record.extraction_status_message}
                        </Typography>
                      )}
                    </Box>
                  )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handlePreview}
                  disabled={previewLoading}
                  sx={{
                    minWidth: 96,
                    px: 1.5,
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {previewLoading ? 'Opening...' : 'Preview'}
                </Button>
                {canRetry && (
                  <Button
                    variant="contained"
                    color={
                      record.status === 'uploaded' ||
                      record.status === 'review_required'
                        ? 'primary'
                        : 'error'
                    }
                    size="small"
                    startIcon={
                      <ArrowPathIcon
                        className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
                      />
                    }
                    onClick={handleRetry}
                    disabled={loading}
                    sx={{
                      minWidth: 96,
                      px: 1.5,
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Retry
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

const ContractStatusPolling = () => {
  const record = useRecordContext()
  const refresh = useRefresh()

  React.useEffect(() => {
    if (!record) return undefined

    const inProgressStatuses = ['uploaded', 'processing']
    if (!inProgressStatuses.includes(record.status)) return undefined

    const intervalId = window.setInterval(() => {
      refresh()
    }, 4000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [record, record?.status, refresh])

  return null
}

const ConfidenceValue = ({ record, source }) => {
  const fieldName =
    source && source.includes('.') ? source.split('.').pop() : source
  const score = record && fieldName ? record[fieldName] : 0
  const confidence = Math.round((score || 0) * 100)
  const color = getConfidenceColor(confidence)

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {confidence}%
      </Typography>
    </Box>
  )
}

const CreateVendorButton = ({ contractId, supplierName, currentVendorId }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [loading, setLoading] = React.useState(false)

  // Don't show if already linked to a vendor
  if (currentVendorId || !supplierName || supplierName === 'None') return null

  const handleCreateVendor = () => {
    setLoading(true)
    dataProvider
      .createVendorFromContract(contractId, supplierName)
      .then((response) => {
        notify(response.message, { type: 'success' })
        refresh()
      })
      .catch((error) => {
        notify(`Error creating vendor: ${error.message}`, { type: 'warning' })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      onClick={handleCreateVendor}
      disabled={loading}
      sx={{ mt: 1 }}
    >
      {loading ? 'Creating...' : 'Create Vendor from This Name'}
    </Button>
  )
}

const MetadataIterator = (props) => {
  return (
    <SimpleFormIterator {...props} disableAdd disableRemove>
      <FormDataConsumer>
        {({ getSource, scopedFormData, formData }) => {
          const fieldValueSource = getSource
            ? getSource('field_value')
            : undefined
          const confidenceSource = getSource
            ? getSource('confidence_score')
            : undefined
          const evidenceSource = getSource
            ? getSource('evidence_quote')
            : undefined
          const verifiedSource = getSource
            ? getSource('is_verified')
            : undefined

          const isMandatory = isFieldMandatory(scopedFormData?.field_name)
          const isSupplierName = scopedFormData?.field_name === 'Supplier Name'

          return (
            <Box
              sx={{
                border: isMandatory ? '1px solid #f59e0b' : '1px solid #e5e7eb',
                borderLeft: isMandatory
                  ? '4px solid #d97706'
                  : '1px solid #e5e7eb',
                borderRadius: 2,
                mb: 2.5,
                px: 2,
                py: 2,
                width: '100%',
                backgroundColor: isMandatory ? '#fffbeb' : '#ffffff',
              }}
            >
              <Grid
                container
                spacing={3}
                alignItems="start"
                sx={{ width: '100%' }}
              >
                <Grid item xs={12} lg={3}>
                  <Labeled
                    label={<MetadataFieldLabel field={scopedFormData} />}
                    sx={{ '& .MuiFormLabel-root': { mb: 1 } }}
                  >
                    <Box sx={{ pt: 0.5 }}>
                      {confidenceSource && (
                        <ConfidenceValue
                          source={confidenceSource}
                          record={scopedFormData}
                        />
                      )}
                    </Box>
                  </Labeled>
                </Grid>
                <Grid item xs={12} lg={7}>
                  {fieldValueSource && (
                    <>
                      <TextInput
                        source={fieldValueSource}
                        label="Extracted Value"
                        multiline
                        fullWidth
                        sx={{
                          '& .MuiInputBase-root': {
                            minHeight: '56px',
                            backgroundColor: '#fff',
                          },
                        }}
                      />
                      {isSupplierName && scopedFormData?.is_verified && (
                        <CreateVendorButton
                          contractId={formData.id}
                          supplierName={scopedFormData?.field_value}
                          currentVendorId={formData.vendor_id}
                        />
                      )}
                      {evidenceSource && (
                        <TextInput
                          source={evidenceSource}
                          label="Source Evidence"
                          multiline
                          fullWidth
                          disabled
                          sx={{
                            mt: 1.5,
                            '& .MuiInputBase-root': {
                              minHeight: '56px',
                              backgroundColor: '#f9fafb',
                            },
                          }}
                        />
                      )}
                    </>
                  )}
                </Grid>
                <Grid item xs={12} lg={2}>
                  <Box
                    sx={{
                      mt: { xs: 0.5, lg: 2.5 },
                      display: 'flex',
                      justifyContent: { xs: 'flex-start', lg: 'center' },
                      alignItems: 'center',
                      minHeight: 56,
                    }}
                  >
                    {verifiedSource && (
                      <BooleanInput source={verifiedSource} label="Verify" />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )
        }}
      </FormDataConsumer>
    </SimpleFormIterator>
  )
}

const ContractMetadataPanel = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [retryLoading, setRetryLoading] = React.useState(false)
  const [previewLoading, setPreviewLoading] = React.useState(false)
  const metadataFields = React.useMemo(
    () => record?.metadata_fields ?? [],
    [record?.metadata_fields]
  )
  const hasMetadata = metadataFields.length > 0
  const terminalNoDataFailure =
    record?.status === 'extraction_failed' &&
    typeof record?.extraction_error_message === 'string' &&
    (record.extraction_error_message.includes(
      'No usable metadata values extracted from document'
    ) ||
      record.extraction_error_message.includes(
        'Unsupported PDF portfolio format'
      ))

  React.useEffect(() => {
    if (hasMetadata) {
      let needsSorting = false
      let i = 1
      while (i < metadataFields.length) {
        if (
          getMetadataSortRank(metadataFields[i - 1]) >
          getMetadataSortRank(metadataFields[i])
        ) {
          needsSorting = true
          break
        }
        i += 1
      }

      if (needsSorting) {
        metadataFields.sort(
          (a, b) => getMetadataSortRank(a) - getMetadataSortRank(b)
        )
      }
    }
  }, [hasMetadata, metadataFields])

  if (!record) return null

  const handleRetryExtraction = () => {
    setRetryLoading(true)
    dataProvider
      .retryExtraction(record.id)
      .then(() => {
        notify('AI extraction retried', { type: 'info' })
        refresh()
      })
      .catch((error) => {
        notify(`Error retrying extraction: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setRetryLoading(false)
      })
  }

  const handlePreview = () => {
    if (!record?.id || previewLoading) return
    setPreviewLoading(true)
    dataProvider
      .getContractPreviewBlob(record.id)
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob)
        window.open(blobUrl, '_blank', 'noopener,noreferrer')
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
      })
      .catch((error) => {
        notify(`Error opening preview: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setPreviewLoading(false)
      })
  }

  return (
    <Box sx={{ mt: 4, mb: 2, overflow: 'hidden', width: '100%' }}>
      <Typography
        variant="h6"
        sx={{
          borderBottom: '1px solid #e5e7eb',
          pb: 1,
          mb: 2,
          fontWeight: 700,
        }}
      >
        Extracted Metadata
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ display: 'block', mb: 1.5 }}
      >
        Core Review fields are part of the standard checklist. Some may be
        legitimately not present for this contract type.
      </Typography>
      {!hasMetadata && (
        <Box
          sx={{
            py: 2,
            px: 2.5,
            mb: 2,
            border: '1px solid #fde68a',
            borderRadius: 1.5,
            backgroundColor: '#fffbeb',
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: '#78350f' }}
          >
            No fields were extracted from this document.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.75 }}>
            {terminalNoDataFailure
              ? 'This file is either an unsupported PDF portfolio or has no usable metadata values. Retrying the same file is unlikely to help. Extract inner documents or save as a standard PDF, then re-upload.'
              : 'Retry AI extraction to repopulate metadata fields before review.'}
          </Typography>
          {terminalNoDataFailure && (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handlePreview}
                disabled={previewLoading}
              >
                {previewLoading ? 'Opening...' : 'Preview PDF'}
              </Button>
            </Box>
          )}
          {!terminalNoDataFailure && (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={
                  <ArrowPathIcon
                    className={`h-4 w-4 ${retryLoading ? 'animate-spin' : ''}`}
                  />
                }
                onClick={handleRetryExtraction}
                disabled={retryLoading}
              >
                Retry AI Extraction
              </Button>
            </Box>
          )}
        </Box>
      )}
      <ArrayInput
        source="metadata_fields"
        label=""
        sx={{
          '& .MuiFormGroup-root': {
            width: '100%',
            px: 0,
            pt: 1,
          },
          '& .RaSimpleFormIterator-root': { width: '100%' },
          '& .RaSimpleFormIterator-list': { width: '100%' },
          '& .RaSimpleFormIterator-form': { width: '100%' },
          '& .RaSimpleFormIterator-line': {
            border: 'none',
            width: '100%',
            maxWidth: 'none',
            display: 'block',
          },
          '& .RaSimpleFormIterator-index': { display: 'none' },
          '& .RaSimpleFormIterator-action': { display: 'none' },
        }}
      >
        <MetadataIterator />
      </ArrayInput>

      <FormDataConsumer>
        {({ formData }) => {
          if (formData.status !== 'extraction_failed') return null
          return (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: '#fef2f2',
                border: '1px solid #fee2e2',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" color="error" gutterBottom>
                Extraction Error
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formData.extraction_error_message ? (
                  <span>{formData.extraction_error_message}</span>
                ) : (
                  <span>
                    The AI extraction failed. Common causes are unreadable PDF
                    text, no usable fields extracted, or provider/rate-limit
                    issues. You can retry using the &quot;Retry AI&quot; button
                    in the header.
                  </span>
                )}
              </Typography>
            </Box>
          )
        }}
      </FormDataConsumer>
    </Box>
  )
}

const RELATIONSHIP_TYPE_CHOICES = [
  'SOW_OF',
  'AMENDMENT_TO',
  'RENEWAL_OF',
  'SUPPLEMENT_TO',
  'LINKED_TO',
]

type ContractRelationshipRow = {
  id: number
  relationship_type: string
  direction: 'parent' | 'child'
  related_contract_id: number
  related_contract_filename: string
  related_contract_status: string
  created_at: string
}

type ContractLinkOption = {
  id: number
  filename: string
  status: string
  contract_type?: string | null
}

type ContractRelationshipSuggestion = {
  related_contract_id: number
  related_contract_filename: string
  related_contract_status: string
  related_contract_type?: string | null
  score: number
  reasons: string[]
}

const getMetadataFieldValue = (record, fieldName) => {
  if (!record || !Array.isArray(record.metadata_fields)) return ''
  const row = record.metadata_fields.find(
    (item) => item?.field_name === fieldName
  )
  if (!row?.field_value) return ''
  const value = String(row.field_value).trim()
  if (!value || value === 'None' || value === 'null') return ''
  return value
}

const ContractRelationshipsPanel = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const redirect = useRedirect()
  const [rows, setRows] = React.useState<ContractRelationshipRow[]>([])
  const [loading, setLoading] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [deletingId, setDeletingId] = React.useState<number | null>(null)
  const [relatedId, setRelatedId] = React.useState('')
  const [selectedLink, setSelectedLink] =
    React.useState<ContractLinkOption | null>(null)
  const [linkOptions, setLinkOptions] = React.useState<ContractLinkOption[]>([])
  const [linkQuery, setLinkQuery] = React.useState('')
  const [loadingOptions, setLoadingOptions] = React.useState(false)
  const [missingParentSuggestions, setMissingParentSuggestions] =
    React.useState<ContractRelationshipSuggestion[]>([])
  const [loadingSuggestions, setLoadingSuggestions] = React.useState(false)
  const [linkingSuggestionId, setLinkingSuggestionId] = React.useState<
    number | null
  >(null)
  const [relationshipType, setRelationshipType] = React.useState('SOW_OF')
  const [role, setRole] = React.useState('child')
  const recordId = record?.id

  const loadRelationships = React.useCallback(() => {
    if (!recordId) return
    setLoading(true)
    dataProvider
      .getContractRelationships(recordId)
      .then((result) => {
        setRows(
          Array.isArray(result) ? (result as ContractRelationshipRow[]) : []
        )
      })
      .catch((error) => {
        notify(`Error loading relationships: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [dataProvider, notify, recordId])

  React.useEffect(() => {
    loadRelationships()
  }, [loadRelationships])

  const parentAgreementReference = getMetadataFieldValue(
    record,
    'Parent Agreement'
  )
  const supplierName = getMetadataFieldValue(record, 'Supplier Name')
  const hasParentLink = rows.some((row) => row.direction === 'parent')
  const showMissingParentAlert =
    Boolean(parentAgreementReference) && !hasParentLink

  React.useEffect(() => {
    if (!recordId) return undefined
    const timer = window.setTimeout(() => {
      setLoadingOptions(true)
      dataProvider
        .getList('contracts', {
          pagination: { page: 1, perPage: 25 },
          sort: { field: 'updated_at', order: 'DESC' },
          filter: linkQuery.trim()
            ? {
                q: linkQuery.trim(),
                ...(supplierName ? { supplier_name: supplierName } : {}),
              }
            : supplierName
            ? { supplier_name: supplierName }
            : {},
        })
        .then((response) => {
          const options = (response?.data || [])
            .filter((row) => row?.id && row.id !== recordId)
            .map((row) => ({
              id: row.id,
              filename: row.filename || `Contract ${row.id}`,
              status: row.status || 'unknown',
              contract_type: row.contract_type || null,
            }))
          setLinkOptions(options)
        })
        .catch(() => {
          setLinkOptions([])
        })
        .finally(() => {
          setLoadingOptions(false)
        })
    }, 250)

    return () => {
      window.clearTimeout(timer)
    }
  }, [dataProvider, linkQuery, recordId, supplierName])

  const handleCreate = () => {
    const selectedId = selectedLink?.id
    const parsedId = Number.parseInt(String(selectedId || relatedId), 10)
    if (!Number.isFinite(parsedId) || parsedId <= 0) {
      notify('Select a related contract or enter a valid ID', {
        type: 'warning',
      })
      return
    }
    setSubmitting(true)
    dataProvider
      .createContractRelationship(record.id, {
        related_contract_id: parsedId,
        relationship_type: relationshipType,
        current_contract_role: role,
      })
      .then(() => {
        notify('Relationship saved', { type: 'success' })
        setRelatedId('')
        setSelectedLink(null)
        setLinkQuery('')
        loadRelationships()
        refresh()
      })
      .catch((error) => {
        notify(`Error saving relationship: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const createParentLinkFromSuggestion = (suggestedContractId: number) => {
    setLinkingSuggestionId(suggestedContractId)
    dataProvider
      .createContractRelationship(recordId, {
        related_contract_id: suggestedContractId,
        relationship_type: 'SOW_OF',
        current_contract_role: 'child',
      })
      .then(() => {
        notify('Parent link created', { type: 'success' })
        loadRelationships()
        refresh()
      })
      .catch((error) => {
        notify(`Error linking parent: ${error.message}`, { type: 'warning' })
      })
      .finally(() => {
        setLinkingSuggestionId(null)
      })
  }

  const handleDelete = (relationshipId) => {
    setDeletingId(relationshipId)
    dataProvider
      .deleteContractRelationship(record.id, relationshipId)
      .then(() => {
        notify('Relationship removed', { type: 'info' })
        loadRelationships()
        refresh()
      })
      .catch((error) => {
        notify(`Error removing relationship: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setDeletingId(null)
      })
  }

  React.useEffect(() => {
    if (!showMissingParentAlert) {
      setMissingParentSuggestions([])
      return undefined
    }
    const queryText = parentAgreementReference.trim()
    if (!queryText) {
      setMissingParentSuggestions([])
      return undefined
    }

    setLoadingSuggestions(true)
    dataProvider
      .getContractRelationshipSuggestions(recordId, 5)
      .then((response) => {
        const options = Array.isArray(response)
          ? (response as ContractRelationshipSuggestion[])
          : []
        setMissingParentSuggestions(options)
      })
      .catch(() => {
        setMissingParentSuggestions([])
      })
      .finally(() => {
        setLoadingSuggestions(false)
      })
    return undefined
  }, [dataProvider, parentAgreementReference, recordId, showMissingParentAlert])

  if (!record || !recordId) return null

  const parentRows = rows.filter((row) => row.direction === 'parent')
  const childRows = rows.filter((row) => row.direction === 'child')

  return (
    <Box sx={{ mt: 3, mb: 2, width: '100%', maxWidth: 'none' }}>
      <Typography
        variant="h6"
        sx={{
          borderBottom: '1px solid #e5e7eb',
          pb: 1,
          mb: 2,
          fontWeight: 700,
        }}
      >
        Contract Relationships
      </Typography>
      {showMissingParentAlert && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            border: '1px solid #f59e0b',
            borderRadius: 1.5,
            backgroundColor: '#fffbeb',
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: '#92400e' }}
          >
            Missing Parent Link
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            Parent Agreement extracted as: &quot;{parentAgreementReference}
            &quot; but this contract has no linked parent.
          </Typography>
          <Box sx={{ mt: 1 }}>
            {loadingSuggestions && (
              <Typography variant="caption" color="textSecondary">
                Searching likely parent contracts...
              </Typography>
            )}
            {!loadingSuggestions &&
              missingParentSuggestions.map((option) => (
                <Box
                  key={option.related_contract_id}
                  sx={{
                    py: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    borderTop: '1px solid #fde68a',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      #{option.related_contract_id}{' '}
                      {option.related_contract_filename}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.related_contract_type || 'Unspecified type'} •{' '}
                      {option.related_contract_status || 'unknown'} • score{' '}
                      {option.score}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      createParentLinkFromSuggestion(option.related_contract_id)
                    }
                    disabled={
                      linkingSuggestionId === option.related_contract_id
                    }
                  >
                    {linkingSuggestionId === option.related_contract_id
                      ? 'Linking...'
                      : 'Link'}
                  </Button>
                </Box>
              ))}
            {!loadingSuggestions && missingParentSuggestions.length === 0 && (
              <Typography variant="caption" color="textSecondary">
                No likely parent contract found. Use search below to link
                manually.
              </Typography>
            )}
          </Box>
        </Box>
      )}
      <Stack spacing={1.2}>
        {loading && (
          <Typography variant="body2" color="textSecondary">
            Loading relationships...
          </Typography>
        )}
        {!loading && rows.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            No parent/child links yet.
          </Typography>
        )}
        {!loading && parentRows.length > 0 && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
            Parent Contracts ({parentRows.length})
          </Typography>
        )}
        {!loading &&
          parentRows.map((row) => (
            <Box
              key={row.id}
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: 1.5,
                px: 1.5,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                  <Chip size="small" label="PARENT" color="secondary" />
                  <Chip size="small" label={row.relationship_type} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  #{row.related_contract_id} {row.related_contract_filename}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Status: {row.related_contract_status}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    redirect('edit', 'contracts', row.related_contract_id)
                  }
                >
                  Open
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => handleDelete(row.id)}
                  disabled={deletingId === row.id}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          ))}
        {!loading && childRows.length > 0 && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
            Child Contracts ({childRows.length})
          </Typography>
        )}
        {!loading &&
          childRows.map((row) => (
            <Box
              key={row.id}
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: 1.5,
                px: 1.5,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                  <Chip size="small" label="CHILD" color="default" />
                  <Chip size="small" label={row.relationship_type} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  #{row.related_contract_id} {row.related_contract_filename}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Status: {row.related_contract_status}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    redirect('edit', 'contracts', row.related_contract_id)
                  }
                >
                  Open
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => handleDelete(row.id)}
                  disabled={deletingId === row.id}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          ))}
      </Stack>
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          border: '1px solid #e5e7eb',
          borderRadius: 1.5,
          backgroundColor: '#f9fafb',
          width: '100%',
          maxWidth: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            gap: 1.5,
            alignItems: 'stretch',
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={5}>
                <Autocomplete
                  options={linkOptions}
                  loading={loadingOptions}
                  value={selectedLink}
                  onChange={(event, value) => {
                    setSelectedLink(value)
                    setRelatedId(value?.id ? String(value.id) : '')
                  }}
                  onInputChange={(event, value) => {
                    setLinkQuery(value || '')
                  }}
                  getOptionLabel={(option) =>
                    option ? `#${option.id} ${option.filename}` : ''
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label="Search Related Contract"
                      size="small"
                      placeholder="Type filename or type"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          #{option.id} {option.filename}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {option.contract_type || 'Unspecified type'} •{' '}
                          {option.status || 'unknown'}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <MuiTextField
                  label="Or Enter ID"
                  size="small"
                  fullWidth
                  value={relatedId}
                  onChange={(event) => setRelatedId(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <MuiTextField
                  select
                  label="Current Contract Role"
                  size="small"
                  fullWidth
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                >
                  <MenuItem value="child">
                    Current is child (related is parent)
                  </MenuItem>
                  <MenuItem value="parent">
                    Current is parent (related is child)
                  </MenuItem>
                </MuiTextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <MuiTextField
                  select
                  label="Relationship Type"
                  size="small"
                  fullWidth
                  value={relationshipType}
                  onChange={(event) => setRelationshipType(event.target.value)}
                >
                  {RELATIONSHIP_TYPE_CHOICES.map((choice) => (
                    <MenuItem key={choice} value={choice}>
                      {choice}
                    </MenuItem>
                  ))}
                </MuiTextField>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'stretch', lg: 'flex-end' },
              minWidth: { xs: '100%', lg: 'auto' },
            }}
          >
            <Button
              variant="contained"
              disabled={submitting}
              onClick={handleCreate}
              sx={{
                minWidth: 190,
                height: 40,
                whiteSpace: 'nowrap',
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              {submitting ? 'Saving...' : 'Add Relationship'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const EditActions = () => (
  <TopToolbar>
    <ListButton />
  </TopToolbar>
)

const ContractEditToolbar = (props) => (
  <Toolbar
    {...props}
    sx={{
      px: 0,
      mt: 3,
      display: 'flex',
      justifyContent: 'flex-start',
      gap: 1.5,
    }}
  >
    <SaveButton />
    <DeleteButton mutationMode="pessimistic" />
  </Toolbar>
)

const ContractStatusInput = () => {
  const record = useRecordContext()
  if (!record) return null

  const editableStatuses = ['review_required', 'completed']
  const isEditable = editableStatuses.includes(record.status)

  if (!isEditable) {
    return (
      <SelectInput
        source="status"
        label="Contract Status"
        fullWidth
        disabled
        sx={{
          mb: 0,
          '& .MuiFormControl-root': { mt: 0, mb: 0, width: '100%' },
          '& .MuiInputBase-root': { height: 56 },
        }}
        choices={[
          { id: 'uploaded', name: 'Uploaded' },
          { id: 'processing', name: 'Processing' },
          { id: 'review_required', name: 'Review Required' },
          { id: 'completed', name: 'Verified' },
          { id: 'failed', name: 'Failed' },
          { id: 'extraction_failed', name: 'Extraction Failed' },
        ]}
      />
    )
  }

  return (
    <SelectInput
      source="status"
      label="Contract Status"
      fullWidth
      sx={{
        mb: 0,
        '& .MuiFormControl-root': { mt: 0, mb: 0, width: '100%' },
        '& .MuiInputBase-root': { height: 56 },
      }}
      choices={[
        { id: 'review_required', name: 'Review Required' },
        { id: 'completed', name: 'Verified' },
      ]}
    />
  )
}

export const ContractEdit = () => (
  <Edit mutationMode="pessimistic" actions={<EditActions />}>
    <Box sx={{ p: { xs: 1.5, md: 3 }, width: '100%' }}>
      <ContractSummaryHeader />
      <SimpleForm
        toolbar={<ContractEditToolbar />}
        sx={{
          '& .RaSimpleForm-form': {
            backgroundColor: '#fff',
            width: '100%',
            maxWidth: 'none',
          },
          '& .RaSimpleForm-form > *': {
            width: '100%',
            maxWidth: 'none',
          },
          '& .RaSimpleFormStack-root': {
            width: '100%',
            maxWidth: 'none',
          },
          '& .MuiInputBase-root': { borderRadius: 1.5 },
        }}
      >
        <ContractStatusPolling />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ReferenceInput
              source="vendor_id"
              reference="contracts/vendors/options"
              label="Linked Vendor"
            >
              <SelectInput optionText="name" fullWidth />
            </ReferenceInput>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput source="contract_type" label="Document Type" fullWidth />
          </Grid>
        </Grid>

        <ContractRelationshipsPanel />

        <ContractMetadataPanel />

        <Box
          sx={{
            mt: 6,
            backgroundColor: '#f9fafb',
            p: 4,
            width: '100%',
            borderRadius: 2,
            border: '1px solid #e5e7eb',
          }}
        >
          <Typography
            variant="subtitle2"
            color="textSecondary"
            sx={{ mb: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            Administrative Info
          </Typography>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <ContractStatusInput />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#fff',
                  border: '1px solid #e7e5e4',
                  borderRadius: 1,
                  minHeight: '56px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: 'block', mb: 0.2, lineHeight: 1 }}
                >
                  Uploaded On
                </Typography>
                <DateField
                  source="created_at"
                  showTime
                  sx={{ display: 'block', fontSize: '0.9rem', fontWeight: 600 }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </SimpleForm>
    </Box>
  </Edit>
)
